import * as Utils from '@js/modules/utils';

import api from '@js/actions/api';

import Polyline from '@js/modules/maps/polyline';

import {DefaultRideType, RideTypes} from '@js/components/modules/constants';

const RideMarkersMapModule = {
    _rideMarkersModel: {
        // Rides properties
        ridesData: [],

        // Group Ride layers
        // Shapes:
        // - rideId
        // - startPoint
        // - startMarkerPopup
        // - ride properties
        rideFeatures: [],

        newMarkerRideIds: [],
        removeMarkerRideIds: [],

        currentHoverMarkerId: null,
        currentOpenMarkerId: null,

        popupBuilder: undefined,
        onPopupClose: undefined,

        isMarkerClick: false,
        isMarkerHover: false,

        // Trigger when a marker is clicked
        markerClickCallbacks: [],
        // Trigger when a marker is hovered
        markerHoverCallbacks: []
    },

    plugRidesMarkers(options = {}) {
        if (options.popupBuilder) {
            this._rideMarkersModel.popupBuilder = options.popupBuilder;
        }
        if (options.onPopupClose) {
            this._rideMarkersModel.onPopupClose = options.onPopupClose;
        }

        this._initializeRideMarkers();

        this._model.map.on('click', () => {

            if (this._rideMarkersModel.isMarkerClick) {
                if (!this._rideMarkersModel.isMarkerHover) {
                    this._rideMarkersModel.isMarkerClick = false;
                }
            } else {
                this.closeAllPopups();

                this._handlePopupClose();

                this._rideMarkersModel.currentOpenMarkerId = null;
            }
        });
    },

    displayRideMarkers(params, callback) {
        if (params.strict || Utils.isPresent(params.ridesData)) {
            if (params.clear) {
                this.clearRidesMarkers();
            }

            this._updateRidesData(params.ridesData);

            if (params.fitBounds) {
                this._model.mapBounds = null;
                this._fitMapBounds();
            }

            if (callback) {
                callback();
            }
        } else {
            this._fetchRidesMarkers(params, callback);
        }
    },

    openRideMarkerPopup(rideId, options = {}) {
        if (this._rideMarkersModel.rideFeatures.length > 0) {
            for (let rideFeatureIndex = 0; rideFeatureIndex < this._rideMarkersModel.rideFeatures.length; rideFeatureIndex++) {
                const rideFeature = this._rideMarkersModel.rideFeatures[rideFeatureIndex];

                if (rideFeature.rideId === rideId) {
                    if (options.openPopup) {
                        this.closeAllPopups();

                        if (!rideFeature.startMarkerPopup) {
                            return;
                        }

                        this._model.hasPopup = true;

                        rideFeature.startMarkerPopup
                            .addTo(this._model.map);

                        this._model.openPopups.push(rideFeature.startMarkerPopup);
                    }
                }
            }
        }
    },

    onRideMarkerClick(clickCallback) {
        this._rideMarkersModel.markerClickCallbacks.push(clickCallback);
    },

    onRideMarkerHover(hoverCallback) {
        this._rideMarkersModel.markerHoverCallbacks.push(hoverCallback);
    },

    fitRidesMarkersBounds(bounds) {
        if (this._rideMarkersModel.rideFeatures.length > 0) {
            for (let p = 0; p < this._rideMarkersModel.rideFeatures.length; p++) {
                bounds = bounds.extend(this._rideMarkersModel.rideFeatures[p].startPoint);
            }
        }
    },

    resetRidesMarkersStyle() {
        this._rideMarkersModel.rideFeatures = [];
        this._rideMarkersModel.newMarkerRideIds = this._rideMarkersModel.ridesData.map((ride) => ride.rideId);
        this._rideMarkersModel.removeMarkerRideIds = [];

        this._drawRideMarkers();
    },

    clearRidesMarkers() {
        this._rideMarkersModel.rideFeatures = [];

        this._rideMarkersModel.ridesData = [];

        RideTypes
            .forEach((rideType) => {
                this.resetMapSource(`${rideType}-primary-marker`);
            });
        this.resetMapSource('cluster-marker');
    },

    /* Private Methods
    ******************** */
    _fetchRidesMarkers(params, doneCallback) {
        const requestParam = {
            map: {},
            // ...(params.filter || {})
        };

        if (params.useBounds && Utils.isPresent(this._model.mapBounds)) {
            requestParam.map.bounds = this._formattedBounds();
        }
        if (params && Utils.isPresent(params.filter)) {
            requestParam.filter = params.filter;
        }
        if (params && Utils.isPresent(params.rideIds)) {
            requestParam.filter.rideIds = params.rideIds;
        }

        api.get('/api/v1/rides/tracks', requestParam)
            .then((ridesData) => {
                if (!ridesData || !ridesData.rides) {
                    return;
                }

                this._updateRidesData(ridesData.rides);

                if (typeof (doneCallback) === 'function') {
                    doneCallback(this._rideMarkersModel.ridesData);
                }
            });
    },

    _initializeRideMarkers() {
        RideTypes
            .forEach((rideType) => {
                this.manageMarkerPopup(
                    `${rideType}-primary-marker`,
                    // Click
                    (markerCoordinates, markerProperties) => {
                        this._rideMarkersModel.isMarkerClick = true;

                        const markerFeatureIndex = markerProperties.markerFeatureIndex;
                        if (!this._rideMarkersModel.rideFeatures[markerFeatureIndex]) {
                            return;
                        }

                        const currentRideId = this._rideMarkersModel.rideFeatures[markerFeatureIndex].rideId;

                        this._markerClickFuncs('ride', {
                            markerId: currentRideId,
                            wasMarkerOpen: currentRideId === this._rideMarkersModel.currentOpenMarkerId
                        });

                        this._rideMarkersModel.currentOpenMarkerId = currentRideId;
                    },
                    // Hover
                    (markerCoordinates, markerProperties) => {
                        this._rideMarkersModel.isMarkerHover = true;

                        const markerFeatureIndex = markerProperties.markerFeatureIndex;
                        const currentRideId = this._rideMarkersModel.rideFeatures[markerFeatureIndex]?.rideId;

                        this._rideMarkersModel.currentHoverMarkerId = currentRideId;

                        this.openRideMarkerPopup(currentRideId, {openPopup: true});

                        this._markerHoverFuncs('ride', {
                            markerId: currentRideId,
                            isHover: true
                        });
                    },
                    // Leave
                    () => {
                        this._rideMarkersModel.isMarkerHover = false;
                        this._rideMarkersModel.currentHoverMarkerId = null;

                        if (this._rideMarkersModel.isMarkerClick) {
                            this._rideMarkersModel.isMarkerClick = false;

                            return;
                        }

                        this.closeAllPopups();

                        this._markerHoverFuncs('ride');
                    }
                );
            });
    },

    _updateRidesData(newRidesData) {
        if (this._rideMarkersModel.ridesData.length > 0) {
            const newRideIds = newRidesData.map((ride) => ride.rideId || ride.id);

            this._rideMarkersModel.newMarkerRideIds = newRideIds.filter((newRideId) => !this._rideMarkersModel.ridesData.map((ride) => ride.rideId || ride.id)
                .includes(newRideId));
            this._rideMarkersModel.removeMarkerRideIds = this._rideMarkersModel.ridesData.filter((data) => !newRideIds.includes(data.id || data.rideId))
                .map((ride) => ride.rideId || ride.id);
        } else {
            this._rideMarkersModel.newMarkerRideIds = newRidesData?.map((ride) => ride.rideId || ride.id) || [];
            this._rideMarkersModel.removeMarkerRideIds = [];
        }

        this._rideMarkersModel.ridesData = newRidesData || [];

        if (this._rideMarkersModel.ridesData.length > 0) {
            this._drawRideMarkers();
        } else {
            this.clearRidesMarkers();
        }
    },

    _drawRideMarkers() {
        // establish key points to edit with
        this._setupRideMarkers();

        // draw markers
        this._redrawRideMarkers();
    },

    _setupRideMarkers() {
        if (Utils.isEmpty(this._rideMarkersModel.ridesData)) {
            // Empty set of tracks
            return;
        }

        if (this._rideMarkersModel.removeMarkerRideIds.length > 0) {
            this._rideMarkersModel.removeMarkerRideIds.forEach((removeRideId) => {
                const rideFeature = this._rideMarkersModel.rideFeatures.find((feature) => feature.rideId === removeRideId);
                if (rideFeature && rideFeature.startMarkerPopup) {
                    rideFeature.startMarkerPopup.remove();
                    rideFeature.startMarkerPopup = undefined;
                }
            });

            this._rideMarkersModel.rideFeatures = this._rideMarkersModel.rideFeatures.filter((feature) => !this._rideMarkersModel.removeMarkerRideIds.includes(feature.rideId));
        }

        for (let trackIndex = 0; trackIndex < this._rideMarkersModel.ridesData.length; trackIndex++) {
            if (Utils.isEmpty(this._rideMarkersModel.ridesData[trackIndex])) {
                continue;
            }

            const currentRideId = this._rideMarkersModel.ridesData[trackIndex].rideId || this._rideMarkersModel.ridesData[trackIndex].id;

            // Check if ride is already loaded
            if (!this._rideMarkersModel.newMarkerRideIds.includes(currentRideId)) {
                continue;
            }

            // Element doesn't exists yet
            const currentTrackFeature = {
                rideId: currentRideId,
                rideType: this._rideMarkersModel.ridesData[trackIndex].rideType.toLowerCase(),
                name: this._rideMarkersModel.ridesData[trackIndex].name,
                defaultPicture: this._rideMarkersModel.ridesData[trackIndex].defaultPicture?.mini?.jpg,
                distance: this._rideMarkersModel.ridesData[trackIndex].distance,
                formattedDistance: this._rideMarkersModel.ridesData[trackIndex].formattedDistance,
                duration: this._rideMarkersModel.ridesData[trackIndex].duration,
                formattedDuration: this._rideMarkersModel.ridesData[trackIndex].formattedDuration,
                level: this._rideMarkersModel.ridesData[trackIndex].level,
                roundTrip: this._rideMarkersModel.ridesData[trackIndex].roundTrip,
                city: this._rideMarkersModel.ridesData[trackIndex].city,
                linkUrl: this._rideMarkersModel.ridesData[trackIndex].link
            };

            const newEncodedPolyline = this._rideMarkersModel.ridesData[trackIndex].encodedPolyline || this._rideMarkersModel.ridesData[trackIndex].simplifiedPolyline;
            if (Utils.isPresent(newEncodedPolyline)) {
                const trackPolyline = Polyline.decode(newEncodedPolyline);
                currentTrackFeature.startPoint = {
                    lat: trackPolyline[0][0],
                    lng: trackPolyline[0][1]
                };
            }

            if (currentTrackFeature.startPoint && this._rideMarkersModel.popupBuilder) {
                currentTrackFeature.startMarkerPopup = this.buildPopup({closeOnClick: false})
                    .setLngLat(currentTrackFeature.startPoint)
                    .setHTML(this._rideMarkersModel.popupBuilder(currentTrackFeature));
                currentTrackFeature.startMarkerPopup.on('close', this._handlePopupClose.bind(this, currentTrackFeature.rideId));
            }

            this._rideMarkersModel.rideFeatures.push(currentTrackFeature);
        }
    },

    _redrawRideMarkers() {
        const mapMarkers = {};

        for (let trackIndex = 0; trackIndex < this._rideMarkersModel.rideFeatures.length; trackIndex++) {
            const currentRideId = this._rideMarkersModel.rideFeatures[trackIndex].rideId;

            // // Check if ride is already loaded
            // if (!this._rideMarkersModel.updatedRideIds.includes(currentRideId)) {
            //     continue;
            // }

            const startPoint = this._rideMarkersModel.rideFeatures[trackIndex].startPoint;

            if (startPoint) {
                const rideType = this._rideMarkersModel.rideFeatures[trackIndex].rideType || DefaultRideType;

                mapMarkers[rideType] = (mapMarkers[rideType] || []).concat({
                    type: 'Feature',
                    properties: {
                        rideId: currentRideId,
                        markerFeatureIndex: trackIndex
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [startPoint.lng, startPoint.lat]
                    }
                });
            }
        }

        Object.keys(mapMarkers)
            .forEach((markerType) => {
                this.updateSource(`${markerType}-primary-marker`, {
                    type: 'FeatureCollection',
                    features: mapMarkers[markerType]
                });
            });
    },

    _markerClickFuncs(type, markerOptions = {}) {
        for (let i = 0, n = this._rideMarkersModel.markerClickCallbacks.length; i < n; i++) {
            if (markerOptions.markerId) {
                this._rideMarkersModel.markerClickCallbacks[i]({
                    type,
                    id: markerOptions.markerId,
                    wasMarkerOpen: markerOptions.wasMarkerOpen
                });
            } else {
                this._rideMarkersModel.markerClickCallbacks[i](null);
            }
        }
    },

    _markerHoverFuncs(type, markerOptions = {}) {
        for (let i = 0, n = this._rideMarkersModel.markerHoverCallbacks.length; i < n; i++) {
            if (markerOptions.markerId) {
                this._rideMarkersModel.markerHoverCallbacks[i]({
                    type,
                    id: markerOptions.markerId
                });
            } else {
                this._rideMarkersModel.markerHoverCallbacks[i](null);
            }
        }
    },

    _handlePopupClose(/* rideId */) {
        if (this._rideMarkersModel.isMarkerHover) {
            return;
        }

        if (this._rideMarkersModel.currentOpenMarkerId) {
            this._markerClickFuncs('ride');
        }
    }
};

export default RideMarkersMapModule;
